// Email Template API - API integration for email templates
// Uses ApiClient with dependency injection pattern

import type { ApiClient } from '@commander/api-client';
import type { EmailTemplate } from '../types';

// Backend response types (snake_case from API)
interface BackendEmailTemplate {
  id: string;
  user_id: string;
  company_id: string | null;
  name: string;
  category?: string;
  subject: string | null;
  body: any; // Lexical JSON with {{handlebar}} variables
  variables: Array<{
    name: string;
    label: string;
    source: string;
    required: boolean;
  }>;
  is_public?: boolean;
  is_active?: boolean;
  times_used: number;
  created_at: string;
  updated_at: string;
}

// Request/response types
export interface EmailTemplateListParams {
  page?: number;
  limit?: number;
  name?: string;
}

export interface EmailTemplateCreateData {
  name: string;
  subject?: string;
  body: any; // Lexical JSON body
  variables?: Array<{
    name: string;
    label: string;
    source: string;
    required?: boolean;
  }>;
  company_id?: string; // For company-wide templates
}

export interface EmailTemplateUpdateData {
  name?: string;
  subject?: string;
  body?: any; // Lexical JSON body
  variables?: Array<{
    name: string;
    label: string;
    source: string;
    required?: boolean;
  }>;
}

/**
 * Transform backend template (snake_case) to frontend format (camelCase)
 */
function transformEmailTemplate(backendTemplate: BackendEmailTemplate): EmailTemplate {
  return {
    id: backendTemplate.id,
    name: backendTemplate.name,
    category: backendTemplate.category || 'general',
    subject: backendTemplate.subject || '',
    body: backendTemplate.body,
    variables: (backendTemplate.variables || []).map((v) => ({
      key: v.name,
      label: v.label,
      type: 'text' as const,
      required: v.required,
    })),
    isPublic: backendTemplate.is_public ?? false,
    isActive: backendTemplate.is_active ?? true,
    usageCount: backendTemplate.times_used,
    createdBy: backendTemplate.user_id,
    createdAt: new Date(backendTemplate.created_at),
    updatedAt: new Date(backendTemplate.updated_at),
  };
}

/**
 * Factory function to create email template API service with ApiClient dependency injection
 * @param apiClient - ApiClient instance from context
 * @returns Service object with all email template API methods
 */
export const createEmailTemplateApi = (apiClient: ApiClient) => ({
  /**
   * Get paginated list of email templates with filtering
   * GET /communications/email_templates
   */
  async getEmailTemplates(params?: EmailTemplateListParams) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.name) queryParams.append('name', params.name);

    const endpoint = `/communications/email_templates${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get<{
      data: BackendEmailTemplate[];
      meta: {
        page: number;
        limit: number;
        total: number;
        total_pages: number;
      };
    }>(endpoint);

    return {
      templates: response.data.map(transformEmailTemplate),
      pagination: response.meta,
    };
  },

  /**
   * Get a single email template by ID
   * GET /communications/email_templates/:emailTemplateId
   */
  async getEmailTemplate(templateId: string) {
    const response = await apiClient.get<{ data: BackendEmailTemplate }>(
      `/communications/email_templates/${templateId}`
    );
    return transformEmailTemplate(response.data);
  },

  /**
   * Create a new email template
   * POST /communications/email_templates
   */
  async createEmailTemplate(data: EmailTemplateCreateData) {
    const response = await apiClient.post<{ data: BackendEmailTemplate }>(
      '/communications/email_templates',
      data
    );
    return transformEmailTemplate(response.data);
  },

  /**
   * Update an existing email template
   * PUT /communications/email_templates/:emailTemplateId
   */
  async updateEmailTemplate(templateId: string, data: EmailTemplateUpdateData) {
    const response = await apiClient.put<{ data: BackendEmailTemplate }>(
      `/communications/email_templates/${templateId}`,
      data
    );
    return transformEmailTemplate(response.data);
  },

  /**
   * Delete an email template (soft delete)
   * DELETE /communications/email_templates/:emailTemplateId
   */
  async deleteEmailTemplate(templateId: string): Promise<void> {
    await apiClient.delete<void>(`/communications/email_templates/${templateId}`);
  },

  /**
   * Render a template for the composer. The server resolves the five system
   * variables from the recipient contact and merges the manually-entered custom
   * variables, so the preview here matches what the recipient receives.
   * POST /communications/email_templates/:emailTemplateId/render
   */
  async renderEmailTemplate(
    templateId: string,
    params: { tenantContactId?: string | null; variables?: Record<string, string> }
  ): Promise<{ subject: string; body: any }> {
    const response = await apiClient.post<{ data: { subject: string; body: any } }>(
      `/communications/email_templates/${templateId}/render`,
      {
        tenantContactId: params.tenantContactId ?? null,
        variables: params.variables ?? {},
      }
    );
    return response.data;
  },
});

// Type for the service instance
export type EmailTemplateApi = ReturnType<typeof createEmailTemplateApi>;
